import React, { useEffect, useState } from 'react';
import Text from 'antd/lib/typography/Text';
import { DefaultOptionType } from 'antd/es/select';
import Select from 'antd/lib/select';
import CheckableTag from 'antd/lib/tag/CheckableTag';
import { Button, ConfigProvider, Flex, Modal } from 'antd/lib';
import { antGrey, getAntdThemeTokens, runwayColors } from '@/components/styles/globalStyles';
import Avatar from 'antd/lib/avatar/avatar';
import { AppPlatform } from '@/types';
import { CloseOutlined, TeamOutlined } from '@ant-design/icons';
import { useThemeMode } from '@/components/styles/themeMode';
import antdTheme from 'antd/lib/theme';

type OrgOverviewTagOption = DefaultOptionType & {
    item: OrgOverviewTagInfo;
};

interface OrgOverviewTagSelectorProps {
    title: string;
    itemsById: Record<string, OrgOverviewTagInfo>;
    selectedTags: Record<string, string>;
    setSelectedTags: (value: Record<string, string>) => void;
    confirmSelectedTags?: (value: Record<string, string>) => void;
}

export interface OrgOverviewTagInfo {
    id: string;
    name: string;
    imageUrl?: string;
    platform?: AppPlatform;
    appId?: string;
    appIds?: string[];
    isUser?: boolean;
    isTeam?: boolean;
    isDestination?: boolean;
}

export function renderTagSelectorSection({
    title,
    itemsById,
    selectedTags,
    setSelectedTags,
    confirmSelectedTags,
}: {
    title: string;
    itemsById: Record<string, OrgOverviewTagInfo>;
    selectedTags: Record<string, string>;
    setSelectedTags: (value: Record<string, string>) => void;
    confirmSelectedTags?: (value: Record<string, string>) => void;
}) {
    if (!itemsById || !Object.keys(itemsById).length) {
        return null;
    }

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                paddingTop: 16,
            }}
        >
            <Text
                style={{
                    fontWeight: 400,
                    color: antGrey.grey6,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontSize: 12,
                }}
            >
                {title}
            </Text>
            <OrgOverviewTagSelector
                title={title}
                itemsById={itemsById}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                confirmSelectedTags={confirmSelectedTags}
            />
        </div>
    );
}

export const OrgOverviewTagSelector = ({
    title,
    itemsById,
    selectedTags,
    setSelectedTags,
    confirmSelectedTags,
}: OrgOverviewTagSelectorProps) => {
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [localSelectedTags, setLocalSelectedTags] = useState<Record<string, string>>(selectedTags);
    const [draftSelectedTags, setDraftSelectedTags] = useState<Record<string, string>>(selectedTags);

    useEffect(() => {
        setLocalSelectedTags(selectedTags);
        setDraftSelectedTags(selectedTags);
    }, [selectedTags]);

    const openModal = () => {
        setDraftSelectedTags(localSelectedTags);
        setFiltersVisible(true);
    };
    const closeModal = () => {
        setDraftSelectedTags(localSelectedTags);
        setFiltersVisible(false);
    };
    const confirmModal = () => {
        setLocalSelectedTags(draftSelectedTags);
        if (confirmSelectedTags) {
            confirmSelectedTags(draftSelectedTags);
        } else {
            setSelectedTags(draftSelectedTags);
        }
        setFiltersVisible(false);
    };

    const { mode } = useThemeMode();
    const isDark = mode === 'dark';

    const getTagSelectorOptions = (isModal?: boolean): OrgOverviewTagOption[] => {
        if (!localSelectedTags) {
            return [];
        }

        return Object.values(itemsById).map((item) => ({
            label: (
                <Flex
                    align="center"
                    style={{
                        gap: 6,
                    }}
                >
                    {item.imageUrl || item.isUser ?
                        <Avatar
                            shape="square"
                            size={12}
                            src={item.imageUrl}
                            style={{
                                borderRadius: 6,
                                border: 'none',
                                flexShrink: 0,
                                backgroundColor:
                                    localSelectedTags[item.id] ? runwayColors.primaryLight : runwayColors.primary,
                                color: runwayColors.white,
                            }}
                        >
                            {item.name?.trim().charAt(0).toUpperCase()}
                        </Avatar>
                    : item.isTeam ?
                        <TeamOutlined />
                    :   null}
                    <Text style={{ color: 'inherit' }} ellipsis={{ tooltip: true }}>
                        {item.name}
                    </Text>
                    {localSelectedTags[item.id] && !isModal ?
                        <CloseOutlined style={{ fontSize: 12 }} />
                    :   null}
                </Flex>
            ),
            value: item.id,
            item,
        }));
    };

    const renderTag = (option: OrgOverviewTagOption) =>
        option.value ?
            <CheckableTag
                checked={!!localSelectedTags[option.value]}
                onChange={(checked) => toggleOption(checked, option)}
                className="tag-selector-tag"
                style={{
                    maxWidth: '100%',
                    marginBottom: 8,
                    marginRight: 6,
                    borderRadius: 2,
                    padding: '1px 8px',
                }}
            >
                {option.label}
            </CheckableTag>
        :   null;

    const toggleOption = (checked: boolean, option: OrgOverviewTagOption) => {
        if (!option.value) {
            return;
        }

        const nextSelectedTags = { ...localSelectedTags };
        if (checked) {
            nextSelectedTags[option.value] = option.value as string;
        } else {
            delete nextSelectedTags[option.value];
        }

        setLocalSelectedTags(nextSelectedTags);
        setSelectedTags(nextSelectedTags);
    };

    const allOptions = getTagSelectorOptions();
    const shouldViewMore = allOptions.length > 5;
    const baseVisibleOptions = shouldViewMore ? allOptions.slice(0, 5) : allOptions;
    const baseVisibleOptionIds = new Set(baseVisibleOptions.map((option) => option.value).filter(Boolean));
    const selectedOverflowOptions = allOptions.filter(
        (option) =>
            !!option.value && !!localSelectedTags[option.value as string] && !baseVisibleOptionIds.has(option.value),
    );
    const optionsToDisplay = [...baseVisibleOptions, ...selectedOverflowOptions];

    return (
        <>
            <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', marginTop: 10 }}>
                {optionsToDisplay.map((option) => renderTag(option))}
            </div>
            {shouldViewMore ?
                <>
                    <Button
                        type="link"
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            padding: 0,
                            color: runwayColors.primaryLight,
                        }}
                        onClick={openModal}
                    >
                        view more...
                    </Button>
                    <ConfigProvider
                        theme={{
                            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                            token: getAntdThemeTokens(isDark ? 'dark' : 'light'),
                        }}
                    >
                        <Modal
                            title={title}
                            open={filtersVisible}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                            }}
                            onClose={closeModal}
                            onOk={confirmModal}
                            onCancel={closeModal}
                            cancelButtonProps={{ style: { display: 'none' } }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    paddingLeft: 16,
                                    paddingRight: 16,
                                    paddingBottom: 18,
                                    maxWidth: 450,
                                }}
                            >
                                <Select
                                    mode="multiple"
                                    style={{ minWidth: 200, flex: 1 }}
                                    value={Object.keys(draftSelectedTags)}
                                    options={getTagSelectorOptions(true)}
                                    optionLabelProp="label"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option?.item.name?.toLowerCase().includes(input.toLowerCase()) ?? false
                                    }
                                    onChange={(nextValues) => {
                                        const previousValues = new Set(Object.keys(draftSelectedTags));
                                        const nextValueSet = new Set(nextValues as string[]);
                                        const nextSelectedTags = { ...draftSelectedTags };

                                        for (const value of nextValues as string[]) {
                                            if (!previousValues.has(value)) {
                                                nextSelectedTags[value] = value;
                                            }
                                        }

                                        for (const value of previousValues) {
                                            if (!nextValueSet.has(value)) {
                                                delete nextSelectedTags[value];
                                            }
                                        }

                                        setDraftSelectedTags(nextSelectedTags);
                                    }}
                                />
                            </div>
                        </Modal>
                    </ConfigProvider>
                </>
            :   null}
        </>
    );
};
